import { Autocomplete, Backdrop, BackdropProps, Box, Button, Modal, Stack, TextField, Typography } from '@mui/material';
import useController from './modal-controller';
import { Spinner } from 'components/design';
import React from 'react';
import { useWorkspace } from 'hooks/useWorkspace';
import { useTranslation } from 'react-i18next';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

const CustomBackdrop = React.forwardRef<HTMLDivElement, BackdropProps>((props, ref) => {
    const { open, ...otherProps } = props;

    return (
        <Backdrop
            ref={ref}
            open={open}
            {...otherProps}
            onClick={(e) => {
                e.stopPropagation(); // Prevent event bubbling to the Modal
            }}
            sx={{
                backdropFilter: 'blur(5px)',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                ...props.sx,
            }}
        />
    );
});

export const WorkspaceModal = ({ handleOpen, handleClose }) => {
    const { t } = useTranslation();
    const { baseWorkspaceUrl, websiteId: webId } = useWorkspace();
    const { isLoading, onChange, websites, onConfirm, websiteId } = useController();

    return (
        <Modal
            open={handleOpen}
            onClose={(event, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                handleClose(event, reason);
            }}
            closeAfterTransition
            slots={{
                backdrop: CustomBackdrop,
            }}
            slotProps={{
                backdrop: {
                    open: handleOpen,
                },
            }}
        >
            {isLoading ? (
                <div>
                    <Spinner />
                </div>
            ) : (
                <Box sx={modalStyle}>
                    <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                        Settings
                    </Typography>
                    <Stack spacing={1}>
                        <Autocomplete
                            key={websiteId}
                            options={websites?.content || []}
                            value={websites?.content.find((website) => website.id === websiteId) ?? undefined}
                            getOptionLabel={(option: { id: string; name: string }) => option.name}
                            renderInput={(params) => (
                                <TextField {...params} label={t('MODAL.WEBSITE_LABEL')} required />
                            )}
                            onChange={(_, value) => onChange(value?.id as string)}
                            disableClearable
                        />
                    </Stack>
                    <Stack direction="row" spacing={2} marginTop={2}>
                        <Button onClick={handleClose} color="primary" variant="outlined" disabled={!baseWorkspaceUrl}>
                            {t('MODAL.CANCEL')}
                        </Button>
                        <Button
                            onClick={() => onConfirm(handleClose)}
                            color="primary"
                            variant="contained"
                            disabled={websiteId === webId}
                        >
                            {t('MODAL.CHANGE')}
                        </Button>
                    </Stack>
                </Box>
            )}
        </Modal>
    );
};
